// ============================================================================
// Merge-variable handling for the sender-side email composer.
//
// The SERVER is the source of truth for how a template is finally rendered —
// see supabase/functions/esign/compose-email.ts. This module exists because
// the composer needs the same substitution rules in the browser to power the
// live preview and the pre-send "this variable won't resolve" warning, and
// the frontend cannot import Deno-side code at build time.
//
// Drift between the two is guarded by merge-vars.test.ts, which imports BOTH
// and asserts the variable lists and substitution behaviour agree. If you add
// a variable, add it in both places or that test fails.
// ============================================================================

export const MERGE_VARIABLES = [
  'recipient_name',
  'document_name',
  'sender_name',
  'company_name',
  'expiration_days',
  'signing_link',
] as const;

export type MergeVariable = typeof MERGE_VARIABLES[number];

/** Human labels for the insert-variable picker. */
export const MERGE_VARIABLE_LABELS: Record<MergeVariable, string> = {
  recipient_name: "Recipient's name",
  document_name: 'Document name',
  sender_name: 'Your name',
  company_name: 'Company name',
  expiration_days: 'Days until the link expires',
  signing_link: 'Signing link',
};

const TOKEN = /\{\{\s*(\w+)\s*\}\}/g;

/** Substitute {{var}} tokens. Unknown tokens are left visible on purpose. */
export function applyMerge(
  template: string,
  vars: Partial<Record<MergeVariable, string>>,
): string {
  return template.replace(
    TOKEN,
    (match, key: string) => (vars as Record<string, string>)[key] ?? match,
  );
}

/** Variables referenced by a template that we cannot resolve at send time. */
export function unresolvedVariables(
  template: string,
  vars: Partial<Record<MergeVariable, string>>,
): string[] {
  const found = new Set<string>();
  for (const m of template.matchAll(TOKEN)) {
    const key = m[1];
    if (!(key in vars) || !(vars as Record<string, string>)[key]) found.add(key);
  }
  return [...found];
}

/** Variables used by a template that are not variables we know about at all. */
export function unknownVariables(template: string): string[] {
  const known = new Set<string>(MERGE_VARIABLES);
  const found = new Set<string>();
  for (const m of template.matchAll(TOKEN)) {
    if (!known.has(m[1] as MergeVariable)) found.add(m[1]);
  }
  return [...found];
}

/** Strip HTML to readable text for the plain-text preview pane. */
export function htmlToPreviewText(html: string): string {
  return html
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/p>/gi, '\n\n')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

// ── guardrails the composer enforces ────────────────────────────────────────

export const SUBJECT_MAX = 100;
export const BODY_MAX = 10_000;
export const NOTE_MAX = 2_000;
